// @flow

import * as React from 'react';
import classNames from 'classnames';
import R from 'ramda';
import { I13nDiv } from 'react-i13n';

import Tooltip from './Tooltip';

type Props = {
  /**
   * checked status (state maintained by parent)
   */
  checked: boolean,
  /**
   * used for input id & data-testid. NOTE: must be unique for each checkbox
   * on the page or they will not work independently.
   */
  id: string,
  /**
   * custom className for outer div
   */
  className?: string,
  /**
   * disable checked status change
   */
  disabled?: boolean,
  /**
   * tooltip to show on hover of a disabled checkbox. Will only appear when
   * 'disabled' field is true.
   */
  disabledTooltip?: string | React.Element<any>,
  /**
   * whether clicking a label checks the box.
   * if this prop is set to true, selecting a label will have no affect on the state of the checkbox
   */
  disableLabelClick?: boolean,
  /**
   * Label for mixpanel tracking
   * NOTE: If no text 'label' prop is passed in, you need to pass
   *       either an i13nLabel or i13nModel.
   */
  i13nLabel?: string,
  /**
   * optional i13nModel - default properties will already be set, so
   * most likely you will only need to add if:
   * 1. there is no string label on your checkbox (then you need to pass a label here)
   * 2. You're adding params - note some default params are added automatically
   * Model passed into the mixpanel event. (Not necessary for most use cases).
   * label attribute can be overridden by i13nLabel.
   *
   * Order of label precendence:
   * 1. i13nLabel
   * 2. i13nModel.label
   * 3. [MOST COMMON] If the label is text, we will default to it.
   */
  i13nModel?: { label?: string, params?: any },
  /**
   * label is what will appear to the right of the checkbox, and can be
   * a string or an element. If no label or your label is not a string,
   * be sure to include an i13nLabel/Model so there is an event name to fire!
   */
  label?: string | React.Element<any>,
  /**
   * custom className for label
   */
  labelClassName?: string,

  /**
   * access checked value in callback with e.target.checked
   */
  onChange?: () => void,
  /**
   * if tooltip and label passed in, an info icon will appear inline next
   * to the label.
   */
  tooltip?: string | React.Element<any>
};

// SUBCOMPONENT OF CHECKBOX:
type labelProps = {
  id: string,
  className?: string,
  disabled?: boolean,
  disableLabelClick?: boolean,
  label?: string | React.Element<any>,
  tooltip?: string | React.Element<any>
};

const Label = (props: labelProps): React.Element<any> => {
  let checkboxLabel = null;
  const textClassName = classNames('check_box--label-text', {
    'check_box--label-text-disable-click': props.disableLabelClick
  });

  if (props.label) {
    checkboxLabel = (
      <span className={classNames(textClassName, props.className)}>
        {props.label}
      </span>
    );
  }

  if (props.label && props.tooltip) {
    checkboxLabel = (
      <span className={textClassName}>
        <span className="check_box--popover-label">
          {props.label}
          <Tooltip body={props.tooltip} infoIconClassName="h_va-u" />
        </span>
      </span>
    );
  }

  return (
    <label
      className={classNames('check_box--label', { disabled: props.disabled })}
      htmlFor={`${props.id}-checkbox`}
    >
      {checkboxLabel}
    </label>
  );
};

class Checkbox extends React.PureComponent<Props> {
  static defaultProps = {
    checked: false,
    disabled: false,
    onChange: () => {},
    i13nModel: {}
  };

  render() {
    /* The Mixpanel event is fired as soon as the click happens, not after the
     * component has updated based on the click. Therefore the i13nModel fired
     * is based on the OLD props, not the new ones.
     *
     * Hence to know if it's now checked after the event, we need the opposite in
     * the 'is_now_checked' field (less confusing than a 'was_checked' field IMO)
     */
    // Set defaults
    let i13nModel = this.props.i13nModel || {};
    let label = this.props.label || '';

    if (!label || typeof label !== 'string') {
      label = this.props.i13nLabel || i13nModel.label || '';
    }

    // Merge params
    const params = R.merge(
      {
        component: 'Checkbox',
        id: this.props.id,
        is_now_checked: !this.props.checked
      },
      i13nModel.params
    );
    // Final merge in case there are other properties passed besides label and params
    i13nModel = R.merge(i13nModel, { label, params });

    let checkbox = (
      <div>
        <input
          className="check_box--input"
          checked={this.props.checked}
          disabled={this.props.disabled}
          id={`${this.props.id}-checkbox`}
          name="check"
          onChange={this.props.onChange}
          type="checkbox"
        />
        <Label
          className={this.props.labelClassName}
          label={this.props.label}
          tooltip={this.props.tooltip}
          disabled={this.props.disabled}
          id={this.props.id}
          disableLabelClick={this.props.disableLabelClick}
        />
      </div>
    );

    if (this.props.disabled && this.props.disabledTooltip) {
      checkbox = (
        <Tooltip body={this.props.disabledTooltip}>{checkbox}</Tooltip>
      );
    }

    return (
      <I13nDiv
        data-testid={`${this.props.id}-checkbox`}
        className={classNames('check_box', this.props.className)}
        i13nModel={i13nModel}
        bindClickEvent={true}
      >
        {checkbox}
      </I13nDiv>
    );
  }
}

export default Checkbox;
